import React, { useState, useEffect } from "react";
import { UrduInput, UrduFieldPair } from "../../../Components/Shared/UrduInput";

const ModalBankAccount = ({ show, close, settingData, handleSave }) => {
  const [formData, setFormData] = useState({
    bankName: "",
    bankName_ur: "",
    accountHolderName: "",
    accountHolderName_ur: "",
    accountNumber: "",
    iban: "",
    branchCode: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (settingData) {
      setFormData(settingData);
    } else {
      setFormData({
        bankName: "",
        bankName_ur: "",
        accountHolderName: "",
        accountHolderName_ur: "",
        accountNumber: "",
        iban: "",
        branchCode: "",
      });
      setErrors({});
    }
  }, [settingData, show]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let filteredValue = value;

    // Apply specific validation rules for each field
    switch (name) {
      case "bankName":
      case "accountHolderName":
        // Only allow alphabets and spaces
        filteredValue = value.replace(/[^a-zA-Z\s]/g, '');
        break;
      case "accountNumber":
      case "branchCode":
        // Only allow numbers
        filteredValue = value.replace(/[^0-9]/g, '');
        break;
      case "iban":
        // Allow alphanumeric characters (no special characters)
        filteredValue = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
        break;
      default:
        break;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: filteredValue,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Bank Name validation
    if (!formData.bankName.trim()) {
      newErrors.bankName = "Bank name is required";
    } else if (formData.bankName.trim().length < 2) {
      newErrors.bankName = "Bank name must be at least 2 characters";
    }

    // Account Holder Name validation
    if (!formData.accountHolderName.trim()) {
      newErrors.accountHolderName = "Account holder name is required";
    } else if (formData.accountHolderName.trim().length < 2) {
      newErrors.accountHolderName = "Account holder name must be at least 2 characters";
    }

    // Account Number validation
    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = "Account number is required";
    } else if (formData.accountNumber.length < 5) {
      newErrors.accountNumber = "Account number must be at least 5 digits";
    }

    // IBAN validation
    if (!formData.iban.trim()) {
      newErrors.iban = "IBAN is required";
    } else if (formData.iban.length < 8) {
      newErrors.iban = "IBAN must be at least 8 characters";
    }

    // Branch Code validation
    if (!formData.branchCode.trim()) {
      newErrors.branchCode = "Branch code is required";
    } else if (formData.branchCode.length < 3) {
      newErrors.branchCode = "Branch code must be at least 3 digits";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      handleSave(formData);
    }
  };

  // Check if all fields are valid
  const isFormValid =
    formData.bankName.trim().length >= 2 &&
    formData.accountHolderName.trim().length >= 2 &&
    formData.accountNumber.length >= 5 &&
    formData.iban.length >= 8 &&
    formData.branchCode.length >= 3;

  if (!show) return null;

  const isEditMode = Boolean(settingData?.id);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-6 text-gray-800">
          {isEditMode ? "Edit Bank Account Details" : "Bank Account Details"}
        </h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Bank Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="bankName"
              value={formData.bankName}
              onChange={handleChange}
              placeholder="Enter bank name"
              className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 transition-all ${errors.bankName
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-blue-500'
              }`}
            />
            {errors.bankName && (
              <p className="text-red-500 text-sm mt-1">{errors.bankName}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              Minimum 2 characters, alphabets and spaces only
            </p>
            <UrduInput
              label="بینک کا نام"
              name="bankName_ur"
              value={formData.bankName_ur}
              onChange={handleChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Holder Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="accountHolderName"
              value={formData.accountHolderName}
              onChange={handleChange}
              placeholder="Enter account holder name"
              className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 transition-all ${errors.accountHolderName
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-blue-500'
              }`}
            />
            {errors.accountHolderName && (
              <p className="text-red-500 text-sm mt-1">{errors.accountHolderName}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              Minimum 2 characters, alphabets and spaces only
            </p>
            <UrduInput
              label="اکاؤنٹ ہولڈر کا نام"
              name="accountHolderName_ur"
              value={formData.accountHolderName_ur}
              onChange={handleChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Account Number <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleChange}
              placeholder="Enter account number"
              className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 transition-all ${errors.accountNumber
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-blue-500'
              }`}
            />
            {errors.accountNumber && (
              <p className="text-red-500 text-sm mt-1">{errors.accountNumber}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              Minimum 5 digits, numbers only
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              IBAN <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="iban"
              value={formData.iban}
              onChange={handleChange}
              placeholder="Enter IBAN"
              className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 transition-all ${errors.iban
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-blue-500'
              }`}
            />
            {errors.iban && (
              <p className="text-red-500 text-sm mt-1">{errors.iban}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              Minimum 8 characters, letters and numbers only (no special characters)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Branch Code <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="branchCode"
              value={formData.branchCode}
              onChange={handleChange}
              placeholder="Enter branch code"
              className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 transition-all ${errors.branchCode
                ? 'border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:ring-blue-500'
              }`}
            />
            {errors.branchCode && (
              <p className="text-red-500 text-sm mt-1">{errors.branchCode}</p>
            )}
            <p className="text-gray-500 text-xs mt-1">
              Minimum 3 digits, numbers only
            </p>
          </div>
        </div>
        <div className="flex justify-end mt-6 gap-3">
          <button
            onClick={close}
            className="btn btn-reset-filter justify-center"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isFormValid}
            className={`btn justify-center ${
              isEditMode ? "btn-edit text-white" : "btn-primary"
            } ${!isFormValid ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            {isEditMode ? "Update" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModalBankAccount;
